import { useRef, useState } from "react";
import { MarkQuestionsAsAnswered } from "../../../../../../../connection/moderator";
import { showAlert } from "../../../../../../general-ui/alert/alert-service";
import { questionsAnsweredBulk } from "../../panel/contexts/engage/questions-state";
import { LanguagesAbbr, PaginatedQuestion } from "../../../../../../../types/working-model";

type UseBulkAnswerParams = {
	token: string | null | undefined;
	language: LanguagesAbbr;
	session_uuid: string | undefined;
	questions: PaginatedQuestion[];
	dispatch: React.Dispatch<any>;
};

export const useBulkAnswer = ({ token, language, session_uuid, questions, dispatch }: UseBulkAnswerParams) => {
	const [selecting, setSelecting] = useState(false);
	const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
	const [bulkAnswering, setBulkAnswering] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);

	const startSelecting = () => {
		setSelecting(prev => !prev);
		setSelectedQuestions(questions.reduce<number[]>((acc, cur) => cur.answered ? [...acc, cur.id] : acc, []));
	};

	const cancelSelecting = () => {
		setSelecting(false);
		setSelectedQuestions([]);
	};

	const handleBulkAnswer = async () => {
		try {
			if (!token || !language || !session_uuid) return;

			// capture before clearing selection state
			const markedIds = [...selectedQuestions];
			const unmarkedIds = questions.filter(q => !markedIds.includes(q.id)).map(q => q.id);

			// apply optimistic update and clear selection UI in one batch,
			// so cards don't remain in checkbox mode during the async API calls
			setBulkAnswering(true);
			dispatch(questionsAnsweredBulk(markedIds, true));
			dispatch(questionsAnsweredBulk(unmarkedIds, false));
			setSelectedQuestions([]);
			setSelecting(false);

			await Promise.all([
				MarkQuestionsAsAnswered({ token, question_ids: markedIds, language, session_uuid, answered: true }),
				MarkQuestionsAsAnswered({ token, question_ids: unmarkedIds, language, session_uuid, answered: false }),
			]);
		} catch (e) {
			console.error(e);
			showAlert({
				message: "Error marking questions as answered",
				description: "We ran into an issue marking the questions as answered. Please wait and try again.",
				type: "error"
			});
		} finally {
			setBulkAnswering(false);
		}
	};

	return {
		panelRef,
		selecting,
		selectedQuestions,
		setSelectedQuestions,
		bulkAnswering,
		startSelecting,
		cancelSelecting,
		handleBulkAnswer,
	};
};
